import {
    AmplitudeFlagsModule,
    IAmplitudeFlagsContract,
    PlanReadyStartLessonArm
} from "@features/amplitude-flags/model";
import { IAppContract } from "@features/app/model";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, createEffect, sample } from "effector";
import { delay } from "patronum";
import { IOnboardingContract } from "../model";
import {
    TStudentPersonalApi,
    PersonalProfileResponse,
    LearningPlanResponse
} from "@features/shared/api/student-personal.api";
import { HttpRequest } from "@features/shared/request/model";
import {
    personalProfileClickEditConfirm,
    personalProfileEdited,
    personalProfileEditFailed,
    personalPlanClickEditConfirm,
    personalPlanEdited,
    personalPlanEditFailed
} from "@features/shared/events";
import { ISubscriptionContract } from "@features/subscription/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { ICoursesContract } from "@features/courses/model";
import { IPlatformContextContract } from "@features/launch-context/model";
import { ICourse } from "@features/shared/entities/courses";
import { $introLessons } from "@features/personal-plan/model";
import { StudentApi } from "@features/shared/api/course-data.api";
import { UserOnboardingAnswer } from "@features/shared/entities/student";

const PRODUCT_V3_DIRECT_CHALLENGE_LOCK_STORAGE_KEY = "product_v3_direct_challenge_locked";
const PRODUCT_V3_DIRECT_CHALLENGE_STARTED_STORAGE_KEY = "product_v3_direct_challenge_started";
export const PRODUCT_V3_DIRECT_CHALLENGE_STARTED_METADATA_KEY = "productV3DirectChallengeStartedAt";

const readLocalStorageFlag = (key: string): boolean => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(key) === "true";
};

type ProductOnboardingLaunchSource = {
    ec: Array<{ [key: string]: unknown }>;
    el: Array<{ lesson_id: number }>;
    introLessons: Array<{ lessonId: number }> | null;
    ecLoading: boolean;
    elLoading: boolean;
    metadata: Record<string, unknown> | null;
    metadataLoading: boolean;
    metadataInitialized: boolean;
    flagsLoaded: boolean;
    student: unknown;
    isApp: boolean;
    // PRD-015 gate inputs (see isPlanReadyArmSettled / isHeldByRouteForPlanReady)
    planReadyArm: PlanReadyStartLessonArm | null;
    activeRoute: string;
};

const hasDirectChallengeEntryStarted = (metadata: Record<string, unknown> | null): boolean => {
    if (readLocalStorageFlag(PRODUCT_V3_DIRECT_CHALLENGE_STARTED_STORAGE_KEY)) return true;

    const value = metadata?.[PRODUCT_V3_DIRECT_CHALLENGE_STARTED_METADATA_KEY];

    return value !== undefined && value !== null && `${value}`.length > 0;
};

const shouldOpenProductOnboardingModal = ({
    ec,
    el,
    introLessons,
    ecLoading,
    elLoading,
    metadata,
    metadataLoading,
    metadataInitialized,
    flagsLoaded,
    student,
    isApp
}: ProductOnboardingLaunchSource) => {
    if (typeof window === "undefined") return false;
    if (student === null) return false;

    // Do not decide on onboarding until experiment flags are fully loaded
    if (!flagsLoaded) return false;

    // Wait for all data to be loaded; and ensure metadata at least started init
    if (!metadataInitialized) return false;
    if (ecLoading || elLoading || metadataLoading) return false;

    const flag = localStorage.getItem("productOnboardingFinished");
    const metadataFlag = metadata?.productOnboardingFinished;

    const productOnboardingFinished = flag !== null || metadataFlag !== undefined;
    const introLessonIds = new Set((introLessons ?? []).map((lesson) => lesson.lessonId));
    const lessonsEmpty =
        Array.isArray(el) && (el.length === 0 || (el.length === 1 && introLessonIds.has(el[0].lesson_id)));
    const isEmpty = Array.isArray(ec) && ec.length === 0 && lessonsEmpty;

    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get("source");
    const isOnLessonPage = window.location.pathname.includes("/lesson/");
    const isProductV3Active = sessionStorage.getItem("product_v3_onboarding_active") === "true";

    if (source === "product_v3_onboarding") {
        return false;
    }

    const isFinished = sessionStorage.getItem("product_v3_finished") === "true";
    if (isFinished) {
        return false;
    }

    if (isOnLessonPage && isProductV3Active) {
        return false;
    }

    const firstLoginFlag = localStorage.getItem("first_login");
    const isUpsellWasPresented = firstLoginFlag === null;

    if (isUpsellWasPresented && !isApp && metadata?.upsells === undefined) {
        return false;
    }

    // In the app there are no upsells, so first_login is never cleared; allow onboarding when isApp
    const shouldShowByUpsellOrApp = isUpsellWasPresented || isApp;

    // Do not show product onboarding if expired subscription wizard is visible
    const wizardVisible = document.body?.querySelector('[data-expired-wizard="true"]') !== null;

    return !wizardVisible && shouldShowByUpsellOrApp && isEmpty && !productOnboardingFinished;
};

// PRD-015. The modal must not open until the student is bucketed: a test-arm
// student must never see the control screen for a tick. The app (iOS/Android)
// is outside the test and never waits. On web the arm is requested lazily the
// first time the launch conditions hold (see run()) and the flags model
// resolves it to "off" when there is no provider, so this never strands.
// Everything the launch decision reads has arrived; the decision itself may still be "no".
export const isLaunchDataLoaded = ({
    student,
    flagsLoaded,
    metadataInitialized,
    ecLoading,
    elLoading,
    metadataLoading
}: Pick<
    ProductOnboardingLaunchSource,
    "student" | "flagsLoaded" | "metadataInitialized" | "ecLoading" | "elLoading" | "metadataLoading"
>) => student !== null && flagsLoaded && metadataInitialized && !ecLoading && !elLoading && !metadataLoading;

export const isPlanReadyArmSettled = ({
    isApp,
    planReadyArm
}: Pick<ProductOnboardingLaunchSource, "isApp" | "planReadyArm">) => isApp || planReadyArm !== null;

// PRD-015, test arm only. The modal is mounted in the (app) layout and has no
// route guard, so today it can open on top of the change-browser page (Meta
// in-app browsers) and the student sees that page only after finishing the
// onboarding. In the test arm the CTA leaves for the lesson, so the page would
// never be seen: hold the modal while the student is still on it (or on an
// upsell) and open once the route moves on. Control keeps today's order.
export const isHeldByRouteForPlanReady = ({
    planReadyArm,
    activeRoute
}: Pick<ProductOnboardingLaunchSource, "planReadyArm" | "activeRoute">) =>
    planReadyArm === "test" && (activeRoute === "/change-browser" || activeRoute.startsWith("/upsell"));

const openProductOnboardingModal = createEvent<boolean>();
const finishUpsellAndStartProductOnboarding = createEvent();
const firstEntryHandoffSettled = createEvent();
// Longest the cover may stay if the launch data never settles (failed fetch).
const FIRST_ENTRY_HANDOFF_SAFETY_MS = 8000;
export const productOnboardingPersonalProfileGenerationStarted = createEvent();
export const productOnboardingGenerationReset = createEvent();
export const productOnboardingStepShouldAdvance = createEvent();
export const productV3BackToLessonClicked = createEvent(); // Event for back button on step 5
export const resetProductV3BackToLesson = createEvent(); // Reset the back flag
export const setProductV3Active = createEvent(); // Set product_v3 onboarding as active
export const resetProductV3Active = createEvent(); // Reset product_v3 onboarding flag
export const productV3DirectChallengeEntryStarted = createEvent();
export const productV3DirectChallengeEntryHeroCtaClicked = createEvent();
const directChallengeNavigationCompleted = createEvent();
const lockProductV3DirectChallengeEntry = createEvent();
const unlockProductV3DirectChallengeEntry = createEvent();

const $isDirectChallengeNavigationInFlight = createStore(false)
    .on(productV3DirectChallengeEntryStarted, () => true)
    .on(directChallengeNavigationCompleted, () => false);

export const generatePersonalProfileFx = createEffect<void, PersonalProfileResponse>();
export const getPersonalProfileFx = createEffect<void, PersonalProfileResponse>();
export const editPersonalProfileFx = createEffect<string, PersonalProfileResponse>();
export const generateLearningPlanFx = createEffect<void, LearningPlanResponse>();
export const getLearningPlanFx = createEffect<void, LearningPlanResponse>();
export const editLearningPlanFx = createEffect<string, LearningPlanResponse>();

export const $isProductOnboardingModalOpen = createStore(false).on(openProductOnboardingModal, (_, p) => p);
// Between the last upsell and the product onboarding the home page must not show through: the
// layout keeps its loading cover from the funnel's end until the onboarding has opened or has
// been ruled out on the destination route.
export const $isFirstEntryHandoffPending = createStore(false)
    .on(finishUpsellAndStartProductOnboarding, () => true)
    .reset(firstEntryHandoffSettled);
export const $personalProfile = createStore<PersonalProfileResponse | null>(null);
export const $learningPlan = createStore<LearningPlanResponse | null>(null);
export const $isPersonalProfileGenerating = generatePersonalProfileFx.pending;
export const $personalProfileGenerationError = createStore<any>(null);
const $onboardingCourseId = createStore<number | null>(null);
const $onboardingCourse = createStore<ICourse | null>(null);

// Store to track when back button was clicked on product_v3 step 5
export const $isGoingBackToLesson = createStore<boolean>(false)
    .on(productV3BackToLessonClicked, () => true)
    .on(resetProductV3BackToLesson, () => false);

// Store to track if product_v3 onboarding is currently active
// Initialize from sessionStorage if available (survives page refresh)
const initialProductV3State =
    typeof window !== "undefined" ? sessionStorage.getItem("product_v3_onboarding_active") === "true" : false;

export const $isProductV3OnboardingActive = createStore<boolean>(initialProductV3State)
    .on(setProductV3Active, () => true)
    .on(resetProductV3Active, () => false);

const initialProductV3DirectChallengeEntryLockedState = readLocalStorageFlag(
    PRODUCT_V3_DIRECT_CHALLENGE_LOCK_STORAGE_KEY
);

export const $isProductV3DirectChallengeEntryLocked = createStore<boolean>(
    initialProductV3DirectChallengeEntryLockedState
)
    .on(lockProductV3DirectChallengeEntry, () => true)
    .on(unlockProductV3DirectChallengeEntry, () => false);

export const $isProductV3DirectChallengeEntryHomeLocked = createStore<boolean>(false);
const $userOnboardingAnswers = createStore<UserOnboardingAnswer[]>([]);

const getOnboardingAnswersByEmailFx = createEffect(async (email: string) => {
    try {
        const response = await StudentApi.getOnboardingAnswersByEmail(email);
        return response.answers;
    } catch (error: any) {
        if (error?.status === 404) {
            return [];
        }
        throw error;
    }
});

const persistProductV3DirectChallengeEntryLockFx = createEffect((isLocked: boolean) => {
    if (typeof window === "undefined") return;

    if (isLocked) {
        localStorage.setItem(PRODUCT_V3_DIRECT_CHALLENGE_LOCK_STORAGE_KEY, "true");
        return;
    }

    localStorage.removeItem(PRODUCT_V3_DIRECT_CHALLENGE_LOCK_STORAGE_KEY);
});

const markProductV3DirectChallengeEntryStartedFx = createEffect(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem(PRODUCT_V3_DIRECT_CHALLENGE_STARTED_STORAGE_KEY, "true");
    localStorage.removeItem(PRODUCT_V3_DIRECT_CHALLENGE_LOCK_STORAGE_KEY);
});

export const personalProfileClickEditFx = createEffect<string, void>((text) => {
    personalProfileClickEditConfirm(text);
});

export const personalProfileEditedFx = createEffect(() => {
    personalProfileEdited();
});
export const personalProfileEditFailedFx = createEffect(() => {
    personalProfileEditFailed();
});
export const personalPlanClickEditFx = createEffect<string, void>((text) => {
    personalPlanClickEditConfirm(text);
});
export const personalPlanEditedFx = createEffect(() => {
    personalPlanEdited();
});
export const personalPlanEditFailedFx = createEffect(() => {
    personalPlanEditFailed();
});
export const startPersonalProfileFlowFx = createEffect(async () => {
    const profileState = $personalProfile.getState();

    if (profileState?.profile && profileState.profile.goal?.value && profileState.profile.ai_proficiency?.description) {
        return;
    }

    try {
        await getPersonalProfileFx();
        const profileAfterGet = $personalProfile.getState();

        // profile present and is not empty
        if (
            profileAfterGet?.profile &&
            profileAfterGet.profile.goal?.value &&
            profileAfterGet.profile.ai_proficiency?.description
        ) {
            return;
        }

        await generatePersonalProfileFx();
    } catch (error) {
        console.error("Error in personal profile flow:", error);
        await generatePersonalProfileFx();
    }
});
export const startLearningPlanFlowFx = createEffect(async () => {
    const planState = $learningPlan.getState();

    // plan present and is not empty
    if (planState?.plan && planState.plan.planSteps && planState.plan.planSteps.length > 0) {
        return;
    }

    try {
        await getLearningPlanFx();
        const planAfterGet = $learningPlan.getState();

        if (planAfterGet?.plan && planAfterGet.plan.planSteps && planAfterGet.plan.planSteps.length > 0) {
            return;
        }

        await generateLearningPlanFx();
    } catch (error) {
        console.error("Error in learning plan flow:", error);
        await generateLearningPlanFx();
    }
});

const studentPersonalApi = new TStudentPersonalApi(HttpRequest);

generatePersonalProfileFx.use(async () => {
    try {
        return await studentPersonalApi.generatePersonalProfile();
    } catch (e) {
        throw e;
    }
});

getPersonalProfileFx.use(async () => {
    return await studentPersonalApi.getPersonalProfile();
});

editPersonalProfileFx.use(async (editPrompt) => {
    return await studentPersonalApi.editPersonalProfile(editPrompt);
});

generateLearningPlanFx.use(async () => {
    return await studentPersonalApi.generateLearningPlan();
});

getLearningPlanFx.use(async () => {
    return await studentPersonalApi.getLearningPlan();
});

editLearningPlanFx.use(async (editPrompt) => {
    return await studentPersonalApi.editLearningPlan(editPrompt);
});

sample({
    clock: generatePersonalProfileFx.failData,
    fn: (error) => error,
    target: $personalProfileGenerationError
});

sample({
    clock: productOnboardingPersonalProfileGenerationStarted,
    source: $personalProfile,
    filter: (profile) => profile === null,
    target: generatePersonalProfileFx
});

sample({
    clock: [generatePersonalProfileFx.done, generatePersonalProfileFx.fail],
    target: productOnboardingStepShouldAdvance
});

sample({
    clock: [getLearningPlanFx.doneData, generateLearningPlanFx.doneData, editLearningPlanFx.doneData],
    fn: (data) => data,
    target: $learningPlan
});

sample({
    clock: [getPersonalProfileFx.doneData, generatePersonalProfileFx.doneData, editPersonalProfileFx.doneData],
    fn: (data) => data,
    target: $personalProfile
});

sample({
    clock: editPersonalProfileFx,
    fn: (text) => text,
    target: personalProfileClickEditFx
});

sample({
    clock: editPersonalProfileFx.doneData,
    filter: (data) => !data.error,
    target: personalProfileEditedFx
});

sample({
    clock: editPersonalProfileFx.doneData,
    filter: (data) => !!data.error,
    target: personalProfileEditFailedFx
});

sample({
    clock: editPersonalProfileFx.fail,
    target: personalProfileEditFailedFx
});

sample({
    clock: editLearningPlanFx,
    fn: (text) => text,
    target: personalPlanClickEditFx
});

sample({
    clock: editLearningPlanFx.doneData,
    filter: (data) => !data.error,
    target: personalPlanEditedFx
});

sample({
    clock: editLearningPlanFx.doneData,
    filter: (data) => !!data.error,
    target: personalPlanEditFailedFx
});

sample({
    clock: editLearningPlanFx.fail,
    target: personalPlanEditFailedFx
});

sample({
    clock: $isProductV3DirectChallengeEntryLocked,
    target: persistProductV3DirectChallengeEntryLockFx
});

sample({
    clock: productV3DirectChallengeEntryStarted,
    target: markProductV3DirectChallengeEntryStartedFx
});

sample({
    clock: directChallengeNavigationCompleted,
    target: unlockProductV3DirectChallengeEntry
});

const contract = new ModelContract(
    {
        $isProductOnboardingModalOpen,
        $isFirstEntryHandoffPending,
        $isPersonalProfileGenerating,
        $personalProfile,
        $personalProfileGenerationError,
        $learningPlan,
        $isGetPending: getPersonalProfileFx.pending,
        $isGeneratePending: generatePersonalProfileFx.pending,
        $isUpdatePending: editPersonalProfileFx.pending,
        $isGetLearningPlanPending: getLearningPlanFx.pending,
        $isGenerateLearningPlanPending: generateLearningPlanFx.pending,
        $isEditLearningPlanPending: editLearningPlanFx.pending,
        $onboardingCourse,
        $isGoingBackToLesson,
        $isProductV3OnboardingActive,
        $isProductV3DirectChallengeEntryLocked,
        $isProductV3DirectChallengeEntryHomeLocked,
        $userOnboardingAnswers
    },
    {
        openProductOnboardingModal,
        finishUpsellAndStartProductOnboarding,
        productOnboardingPersonalProfileGenerationStarted,
        productV3BackToLessonClicked,
        resetProductV3BackToLesson,
        setProductV3Active,
        resetProductV3Active,
        productV3DirectChallengeEntryStarted,
        productV3DirectChallengeEntryHeroCtaClicked,
        directChallengeNavigationCompleted
    },
    {
        getPersonalProfileFx,
        generatePersonalProfileFx,
        editPersonalProfileFx,
        generateLearningPlanFx,
        getLearningPlanFx,
        editLearningPlanFx,
        startPersonalProfileFlowFx,
        startLearningPlanFlowFx
    },
    {}
);

export type IProductOnboardingContract = typeof contract;

export class ProductOnboardingModule extends ModuleDescription<IProductOnboardingContract> {
    constructor(
        public authModule: IUserAuthContract,
        public enrollmentsModule: IEnrollmentsContract,
        public onboardingModule: IOnboardingContract,
        public subscriptionModule: ISubscriptionContract,
        public metadataModule: IMetadataContract,
        public coursesModule: ICoursesContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public platformContextModule: IPlatformContextContract,
        public appModule: IAppContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: productV3DirectChallengeEntryStarted,
            fn: () => ({
                key: PRODUCT_V3_DIRECT_CHALLENGE_STARTED_METADATA_KEY,
                value: new Date().toISOString()
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // PRD-015: the arm store and the route are clocks on purpose. The arm
        // lands via an effect's doneData (after the data clocks have ticked),
        // and the route updates only after Next commits the navigation (the
        // change-browser "Skip" fires finishUpsellAndStartProductOnboarding
        // while the route is still /change-browser).
        const launchClock = [
            this.subscriptionModule.stores.$paymentSystem,
            this.enrollmentsModule.stores.$enrollmentsCoursesLoading,
            this.enrollmentsModule.stores.$enrollmentsLessonsLoading,
            this.metadataModule.stores.$metadataLoading,
            this.metadataModule.stores.$metadataInitialized,
            this.amplitudeFlagsModule.stores.$flagsLoaded,
            $introLessons,
            finishUpsellAndStartProductOnboarding,
            this.amplitudeFlagsModule.stores.$planReadyStartLessonArm,
            this.appModule.stores.$activeRoute
        ];

        const launchSource = {
            ec: this.enrollmentsModule.stores.$enrollmentsCourses,
            el: this.enrollmentsModule.stores.$enrollmentsLessons,
            introLessons: $introLessons,
            ecLoading: this.enrollmentsModule.stores.$enrollmentsCoursesLoading,
            elLoading: this.enrollmentsModule.stores.$enrollmentsLessonsLoading,
            metadata: this.metadataModule.stores.$metadata,
            metadataLoading: this.metadataModule.stores.$metadataLoading,
            metadataInitialized: this.metadataModule.stores.$metadataInitialized,
            flagsLoaded: this.amplitudeFlagsModule.stores.$flagsLoaded,
            student: this.authModule.stores.$student,
            isApp: this.platformContextModule.stores.$isApp,
            planReadyArm: this.amplitudeFlagsModule.stores.$planReadyStartLessonArm,
            activeRoute: this.appModule.stores.$activeRoute
        };

        // PRD-015: lazy exposure — request the arm the first time the launch
        // conditions hold, web only. Re-firing while the arm is pending is
        // harmless: the flags model latches the request.
        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source) =>
                !source.isApp && source.planReadyArm === null && shouldOpenProductOnboardingModal(source),
            target: this.amplitudeFlagsModule.events.planReadyStartLessonRequested
        });

        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source) =>
                shouldOpenProductOnboardingModal(source) &&
                isPlanReadyArmSettled(source) &&
                !isHeldByRouteForPlanReady(source),
            fn: () => true,
            target: openProductOnboardingModal
        });

        // The handoff is over once the student is on the destination route and the launch
        // decision is final: the onboarding opened (sample above), or the loaded data says it is
        // not needed. Decided on the upsell route it would be wrong: first_login is still set there.
        sample({
            clock: launchClock,
            source: launchSource,
            filter: (source) =>
                !source.activeRoute.startsWith("/upsell") &&
                source.activeRoute !== "/change-browser" &&
                isLaunchDataLoaded(source) &&
                (!shouldOpenProductOnboardingModal(source) ||
                    (isPlanReadyArmSettled(source) && !isHeldByRouteForPlanReady(source))),
            target: firstEntryHandoffSettled
        });
        sample({
            clock: $isProductOnboardingModalOpen.updates,
            filter: (isOpen) => isOpen,
            target: firstEntryHandoffSettled
        });
        sample({ clock: this.authModule.events.logoutStudent, target: firstEntryHandoffSettled });
        delay({
            source: finishUpsellAndStartProductOnboarding,
            timeout: FIRST_ENTRY_HANDOFF_SAFETY_MS,
            target: firstEntryHandoffSettled
        });

        sample({
            clock: [
                this.subscriptionModule.stores.$paymentSystem,
                this.enrollmentsModule.stores.$enrollmentsCoursesLoading,
                this.enrollmentsModule.stores.$enrollmentsLessonsLoading,
                this.metadataModule.stores.$metadataLoading,
                this.metadataModule.stores.$metadataInitialized,
                this.amplitudeFlagsModule.stores.$productOnboardingVariant,
                $introLessons,
                finishUpsellAndStartProductOnboarding
            ],
            source: {
                metadata: this.metadataModule.stores.$metadata,
                isNavigating: $isDirectChallengeNavigationInFlight
            },
            filter: (source) => hasDirectChallengeEntryStarted(source.metadata) && !source.isNavigating,
            target: unlockProductV3DirectChallengeEntry
        });

        sample({
            source: { isOpen: $isProductOnboardingModalOpen },
            filter: (s) => s.isOpen,
            fn: () => "roadmap",
            target: [this.onboardingModule.events.setOnboardingDataById]
        });

        sample({
            clock: this.authModule.events.authenticated,
            target: [getPersonalProfileFx, getLearningPlanFx]
        });

        sample({
            source: {
                onboardingCourseId: $onboardingCourseId,
                courses: this.coursesModule.stores.$courses
            },
            filter: (s) => s.courses.length > 0,
            fn: (s) => {
                if (s.onboardingCourseId === null) {
                    const challenges = s.courses.filter((course) => course.type === "challenge");
                    const challenge = challenges.length > 0 ? challenges[0] : s.courses[0];
                    return challenge;
                }

                const a = s.courses.find((course) => course.id === s.onboardingCourseId);

                return a ?? s.courses[0];
            },
            target: $onboardingCourse
        });

        sample({
            clock: this.authModule.effects.getOnboardingDataFx.doneData,
            fn: (data) => Number(data.courseId),
            target: $onboardingCourseId
        });

        sample({
            source: { student: this.authModule.stores.$student },
            filter: (s) => s.student !== null,
            fn: (s) => s.student!.email,
            target: getOnboardingAnswersByEmailFx
        });

        sample({
            clock: getOnboardingAnswersByEmailFx.doneData,
            target: $userOnboardingAnswers
        });
    }
}
